import React, { useState, useEffect } from 'react';
import { settingsApi, coursesApi, companiesApi } from '../../services/api';
import { Card, CardContent } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ShieldCheck, Clock, UserMinus, Warning, HeartBreak, Lock, Eye, EyeSlash, FloppyDisk, ArrowsClockwise, CalendarCheck, Timer, MagnifyingGlass, Crown } from '@phosphor-icons/react';
import { toast } from 'sonner';

const RULES = [
  {
    key: 'rule_max_attempts',
    icon: ShieldCheck,
    title: 'Maximo 2 intentos por curso',
    description: 'Un trabajador puede ser inscrito hasta 2 veces en un mismo curso (si reprueba). A la tercera, solo el administrador puede asignarlo.',
    color: 'text-blue-500',
    bg: 'bg-blue-500/10',
  },
  {
    key: 'rule_deadline',
    icon: Clock,
    title: 'Cierre de inscripcion a las 12:00 del dia anterior',
    description: 'La inscripcion se cierra a las 12:00 (hora Chile) del dia anterior a la capacitacion. Solo el administrador puede inscribir despues de ese plazo.',
    color: 'text-amber-500',
    bg: 'bg-amber-500/10',
  },
  {
    key: 'rule_active_enrollment',
    icon: Warning,
    title: 'Bloqueo por capacitacion activa',
    description: 'Si un trabajador esta inscrito en una capacitacion del mismo curso que aun no finaliza, no puede ser inscrito en otra hasta que se valide su asistencia.',
    color: 'text-orange-500',
    bg: 'bg-orange-500/10',
  },
  {
    key: 'rule_absence_block',
    icon: UserMinus,
    title: 'Bloqueo por ausencia previa',
    description: 'Si un trabajador tuvo una ausencia en una capacitacion del mismo curso, solo el administrador puede volver a asignarlo.',
    color: 'text-red-500',
    bg: 'bg-red-500/10',
  },
  {
    key: 'rule_life_saving',
    icon: HeartBreak,
    title: 'Reinstruccion por Reglas que Salvan Vidas',
    description: 'Cuando un trabajador tiene un curso vigente pero transgrede una de las "Reglas que Salvan Vidas", permite reinscribirlo obligatoriamente. El administrador debe registrar la transgresion desde la capacitacion.',
    color: 'text-rose-600',
    bg: 'bg-rose-600/10',
  },
  {
    key: 'rule_renewal_window',
    icon: ArrowsClockwise,
    title: 'Ventana de renovacion por vencimiento',
    description: 'Cuando a un trabajador le quedan pocos dias de vigencia en CUALQUIERA de sus capacitaciones, se habilita solicitar/renovar el cupo de esa y de todas las demas. Si a todas les queda mas que ese plazo, permanecen vigentes y no se puede renovar.',
    color: 'text-teal-600',
    bg: 'bg-teal-600/10',
    hasDays: true,
  },
  {
    key: 'rule_same_day',
    icon: CalendarCheck,
    title: 'Mismo dia por trabajador',
    description: 'Un trabajador debe realizar todas sus capacitaciones el mismo dia calendario (hora Chile). No se puede asignarlo/inscribirlo a un curso en un dia distinto al que ya tiene otra capacitacion futura. Aplica a contratistas y administrador.',
    color: 'text-indigo-600',
    bg: 'bg-indigo-600/10',
  },
];

export default function SettingsPage() {
  const [rules, setRules] = useState({});
  const [loading, setLoading] = useState(true);
  const [pdfPassword, setPdfPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  useEffect(() => { fetchRules(); }, []);

  const fetchRules = async () => {
    try {
      const res = await settingsApi.getEnrollmentRules();
      setRules(res.data);
      setPdfPassword(res.data.certificate_pdf_password || '');
    } catch { toast.error('Error al cargar configuracion'); }
    finally { setLoading(false); }
  };

  const toggleRule = async (key) => {
    const newVal = !rules[key];
    const updated = { ...rules, [key]: newVal };
    setRules(updated);
    try {
      await settingsApi.updateEnrollmentRules({ [key]: newVal });
      toast.success(`Regla ${newVal ? 'activada' : 'desactivada'}`);
    } catch {
      setRules(rules);
      toast.error('Error al actualizar regla');
    }
  };

  const saveRenewalDays = async () => {
    const val = parseInt(rules.renewal_window_days);
    const days = Number.isNaN(val) || val < 0 ? 15 : val;
    setRules(r => ({ ...r, renewal_window_days: days }));
    try {
      await settingsApi.updateEnrollmentRules({ renewal_window_days: days });
      toast.success(`Ventana de renovacion: ${days} dias`);
    } catch { toast.error('Error al guardar dias de renovacion'); }
  };

  const handleSavePassword = async () => {
    setSavingPassword(true);
    try {
      await settingsApi.updateEnrollmentRules({ certificate_pdf_password: pdfPassword });
      toast.success(pdfPassword ? 'Clave del PDF actualizada' : 'Proteccion con clave desactivada');
    } catch { toast.error('Error al guardar'); }
    finally { setSavingPassword(false); }
  };

  if (loading) {
    return (<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2BBAA4]" /></div>);
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="settings-page">
      <div>
        <h1 className="text-2xl font-bold text-[#2B4B8C]">Configuracion</h1>
        <p className="text-[#2B4B8C]/60 text-sm">Reglas de negocio y seguridad de certificados</p>
      </div>

      <Tabs defaultValue="reglas" className="w-full">
        <TabsList className="bg-[#f1f4f9]">
          <TabsTrigger value="reglas" data-testid="tab-reglas">Reglas</TabsTrigger>
          <TabsTrigger value="horarios" data-testid="tab-horarios">Horarios de prueba (Auto)</TabsTrigger>
        </TabsList>

        <TabsContent value="reglas" className="space-y-6 mt-4">
      {/* Enrollment Rules */}
      <Card className="bg-white border-[#2B4B8C]/10">
        <CardContent className="p-6">
          <h2 className="text-lg font-semibold text-[#2B4B8C] mb-1">Reglas de Inscripcion</h2>
          <p className="text-sm text-[#2B4B8C]/50 mb-6">
            Active o desactive las reglas que controlan quien puede inscribir trabajadores.
          </p>
          <div className="space-y-4">
            {RULES.map((rule) => {
              const Icon = rule.icon;
              const active = rules[rule.key] ?? true;
              return (
                <div
                  key={rule.key}
                  className={`flex items-start gap-4 p-4 rounded-xl border transition-all ${active ? 'border-[#2B4B8C]/15 bg-[#f8fafc]' : 'border-dashed border-[#2B4B8C]/10 bg-white opacity-60'}`}
                  data-testid={`rule-${rule.key}`}
                >
                  <div className={`w-10 h-10 rounded-lg ${rule.bg} flex items-center justify-center shrink-0 mt-0.5`}>
                    <Icon size={22} className={rule.color} weight="bold" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-[#2B4B8C] text-sm">{rule.title}</p>
                    <p className="text-xs text-[#2B4B8C]/50 leading-relaxed">{rule.description}</p>
                    {rule.hasDays && active && (
                      <div className="flex items-center gap-2 mt-3">
                        <Label className="text-xs text-[#2B4B8C]/70">Dias antes del vencimiento:</Label>
                        <Input
                          type="number"
                          min="0"
                          value={rules.renewal_window_days ?? 15}
                          onChange={(e) => setRules({ ...rules, renewal_window_days: e.target.value === '' ? '' : parseInt(e.target.value) })}
                          onBlur={() => saveRenewalDays()}
                          className="h-8 w-20 bg-white border-[#2B4B8C]/15 text-[#2B4B8C]"
                          data-testid="renewal-window-days"
                        />
                        <span className="text-xs text-[#2B4B8C]/50">dias</span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0 pt-1">
                    <Label className={`text-xs font-medium ${active ? 'text-green-600' : 'text-[#2B4B8C]/30'}`}>
                      {active ? 'Activa' : 'Inactiva'}
                    </Label>
                    <Switch
                      checked={active}
                      onCheckedChange={() => toggleRule(rule.key)}
                      data-testid={`toggle-${rule.key}`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Certificate PDF Password */}
      <Card className="bg-white border-[#2B4B8C]/10">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-[#2B4B8C]/10 flex items-center justify-center shrink-0">
              <Lock size={22} className="text-[#2B4B8C]" weight="bold" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-[#2B4B8C] mb-1">Proteccion de Certificados PDF</h2>
              <p className="text-sm text-[#2B4B8C]/50 mb-4">
                Los certificados PDF se generan con marca de agua y proteccion con clave para evitar adulteracion. Deje en blanco para desactivar la clave.
              </p>
              <div className="flex items-center gap-3 max-w-md">
                <div className="relative flex-1">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={pdfPassword}
                    onChange={(e) => setPdfPassword(e.target.value)}
                    placeholder="Clave del PDF"
                    className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C] pr-10 font-mono"
                    data-testid="pdf-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#2B4B8C]/40 hover:text-[#2B4B8C]/70"
                  >
                    {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                <Button
                  onClick={handleSavePassword}
                  disabled={savingPassword}
                  className="bg-[#2BBAA4] text-black hover:bg-[#239986] shrink-0"
                  data-testid="save-password-btn"
                >
                  <FloppyDisk size={16} className="mr-1.5" />
                  {savingPassword ? 'Guardando...' : 'Guardar'}
                </Button>
              </div>
              {pdfPassword && (
                <p className="text-xs text-[#2BBAA4] mt-2 flex items-center gap-1">
                  <Lock size={12} /> Proteccion activa - Los PDF requeriran esta clave para ser editados
                </p>
              )}
              {!pdfPassword && (
                <p className="text-xs text-amber-500 mt-2 flex items-center gap-1">
                  <Warning size={12} /> Sin clave - Los PDF no tendran proteccion con contrasena
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
        </TabsContent>

        <TabsContent value="horarios" className="mt-4">
          <TestScheduleTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function TestScheduleTab() {
  const [courses, setCourses] = useState([]);
  const [mandantes, setMandantes] = useState([]);
  const [mandanteFilter, setMandanteFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [drafts, setDrafts] = useState({});
  const [savingId, setSavingId] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const [cRes, mRes] = await Promise.all([
        coursesApi.getAll({}),
        companiesApi.getAll({ is_client: true, page_size: 500 }),
      ]);
      const list = cRes.data || [];
      setCourses(list);
      const md = mRes.data?.items || mRes.data || [];
      setMandantes(Array.isArray(md) ? md : []);
      const d = {};
      list.forEach(c => { d[c.id] = { start: c.test_auto_start || '', end: c.test_auto_end || '' }; });
      setDrafts(d);
    } catch { toast.error('Error al cargar cursos'); }
    finally { setLoading(false); }
  };

  const setDraft = (id, field, val) => setDrafts(d => ({ ...d, [id]: { ...d[id], [field]: val } }));

  const save = async (c) => {
    const dr = drafts[c.id] || { start: '', end: '' };
    setSavingId(c.id);
    try {
      await coursesApi.setTestSchedule(c.id, dr.start.trim() || null, dr.end.trim() || null);
      toast.success(dr.start && dr.end ? `Horario guardado (${dr.start}–${dr.end})` : 'Horario eliminado (solo On/Off)');
      setCourses(cs => cs.map(x => x.id === c.id ? { ...x, test_auto_start: dr.start || null, test_auto_end: dr.end || null } : x));
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al guardar horario'); }
    finally { setSavingId(null); }
  };

  const clear = async (c) => {
    setDraft(c.id, 'start', ''); setDraft(c.id, 'end', '');
    setSavingId(c.id);
    try {
      await coursesApi.setTestSchedule(c.id, null, null);
      toast.success('Horario eliminado (solo On/Off)');
      setCourses(cs => cs.map(x => x.id === c.id ? { ...x, test_auto_start: null, test_auto_end: null } : x));
    } catch { toast.error('Error'); }
    finally { setSavingId(null); }
  };

  const filtered = courses.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) &&
    (mandanteFilter === 'all' || c.client_id === mandanteFilter)
  );

  if (loading) {
    return (<div className="flex items-center justify-center h-40"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2BBAA4]" /></div>);
  }

  return (
    <Card className="bg-white border-[#2B4B8C]/10">
      <CardContent className="p-6">
        <div className="flex items-start gap-4 mb-5">
          <div className="w-10 h-10 rounded-lg bg-indigo-500/10 flex items-center justify-center shrink-0">
            <Timer size={22} className="text-indigo-600" weight="bold" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-[#2B4B8C] mb-1">Horarios de prueba (Modo automático)</h2>
            <p className="text-sm text-[#2B4B8C]/50">
              Define por curso la ventana horaria (hora Chile) en la que la prueba se abre automáticamente el día de la capacitación.
              Si un curso no tiene horario, el modo Auto no estará disponible para él (solo On/Off manual).
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1 max-w-sm">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#2B4B8C]/40" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar curso..."
              className="pl-9 bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]"
              data-testid="course-schedule-search"
            />
          </div>
          <Select value={mandanteFilter} onValueChange={setMandanteFilter}>
            <SelectTrigger className="w-full sm:w-72 bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]" data-testid="mandante-filter">
              <div className="flex items-center gap-1.5"><Crown size={14} className="text-[#2B4B8C]/50" /><SelectValue placeholder="Filtrar por mandante" /></div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los mandantes</SelectItem>
              {mandantes.map(m => (<SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1">
          {filtered.length === 0 ? (
            <p className="text-sm text-[#2B4B8C]/40 py-8 text-center">No se encontraron cursos.</p>
          ) : filtered.map(c => {
            const dr = drafts[c.id] || { start: '', end: '' };
            const configured = c.test_auto_start && c.test_auto_end;
            return (
              <div key={c.id} className="flex flex-col md:flex-row md:items-center gap-3 p-3 rounded-xl border border-[#2B4B8C]/10 bg-[#f8fafc]" data-testid={`course-schedule-${c.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-[#2B4B8C] text-sm truncate">{c.name}</p>
                  {configured ? (
                    <p className="text-xs text-green-600 flex items-center gap-1"><Clock size={12} /> Auto: {c.test_auto_start}–{c.test_auto_end} hrs</p>
                  ) : (
                    <p className="text-xs text-[#2B4B8C]/40">Sin horario · Auto no disponible (solo On/Off)</p>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Input type="time" value={dr.start} onChange={(e) => setDraft(c.id, 'start', e.target.value)} className="h-8 w-36 bg-white border-[#2B4B8C]/15 text-[#2B4B8C]" data-testid={`start-${c.id}`} />
                  <span className="text-[#2B4B8C]/40 text-sm">a</span>
                  <Input type="time" value={dr.end} onChange={(e) => setDraft(c.id, 'end', e.target.value)} className="h-8 w-36 bg-white border-[#2B4B8C]/15 text-[#2B4B8C]" data-testid={`end-${c.id}`} />
                  <Button size="sm" onClick={() => save(c)} disabled={savingId === c.id} className="bg-[#2BBAA4] text-black hover:bg-[#239986] h-8" data-testid={`save-schedule-${c.id}`}>
                    <FloppyDisk size={14} className="mr-1" /> Guardar
                  </Button>
                  {configured && (
                    <Button size="sm" variant="ghost" onClick={() => clear(c)} disabled={savingId === c.id} className="text-red-500 hover:text-red-600 h-8 px-2" data-testid={`clear-schedule-${c.id}`}>
                      Quitar
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
